import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getResolvedClosedCountByResponsibleUser } from '../../services/ticketService';
import ResolvedClosedByResponsibleChart from '../../components/dashboards/ResolvedClosedByResponsibleChart';
import DashboardDemandTypeFilter from '../../components/dashboards/DashboardDemandTypeFilter';
import DashboardPeriodFilter from '../../components/dashboards/DashboardPeriodFilter';
import {
  buildPeriodApiParams,
  DEFAULT_PERIOD_PRESET,
} from '../../services/dashboardPeriodService';
import CollapsibleFilters from '../../components/shared/CollapsibleFilters';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ResolvedClosedByResponsiblePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filterDemandType, setFilterDemandType] = useState([]);
  const [periodPreset, setPeriodPreset] = useState(DEFAULT_PERIOD_PRESET);

  const periodParams = useMemo(
    () => buildPeriodApiParams(periodPreset),
    [periodPreset]
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (periodPreset && periodPreset !== DEFAULT_PERIOD_PRESET) n += 1;
    if (Array.isArray(filterDemandType) && filterDemandType.length > 0) n += 1;
    return n;
  }, [periodPreset, filterDemandType]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getResolvedClosedCountByResponsibleUser({
        demandType: filterDemandType,
        from: periodParams.from,
        to: periodParams.to,
      });
      setData(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching resolved/closed by responsible:', err);
      setData([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [filterDemandType, periodParams.from, periodParams.to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <PageContainer>
      <PageTitle>Dashboards • Responsáveis</PageTitle>
      <CollapsibleFilters activeCount={activeFilterCount}>
        <FiltersRow>
          <DashboardPeriodFilter
            value={periodPreset}
            onChange={setPeriodPreset}
            disabled={loading}
            fieldId="dashboard-resolved-period"
          />
          <DashboardDemandTypeFilter
            selectedValues={filterDemandType}
            onValuesChange={setFilterDemandType}
            disabled={loading}
            fieldId="dashboard-resolved-demand-type"
          />
        </FiltersRow>
      </CollapsibleFilters>
      <ResolvedClosedByResponsibleChart
        data={data}
        loading={loading}
        error={error}
        onRetry={fetchData}
        periodLabel={periodParams.label}
        demandTypeFilter={filterDemandType}
      />
    </PageContainer>
  );
};

export default ResolvedClosedByResponsiblePage;
